/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Form, Input, Button, Flex, Select } from 'antd';
import { leadState } from '../../common/constants/leadState';
import 'react-phone-input-2/lib/bootstrap.css';
import classes from '../../common/styles/FormStyles.module.scss';
import { inputStyle } from '../../theming';
import { useTranslation } from 'react-i18next';
import getInputRules, { inputType } from '../../utils/getInputRules';
import { checkValuesChanged, handleFormValuesChange } from './helper';
import LanguageOption from './LanguageOption';
import { languageOptions } from '../../common/constants/languageOptions';

export const JobForm = ({
    mode,
    job,
    loading,
    handleAdd,
    handleEdit,
    openPreviewDrawer,
    isClickFromView,
    statuses,
    onHasUnsavedChanges,
    hasUnsavedChanges,
}) => {
    const [form] = Form.useForm();
    const { t } = useTranslation(['form', 'jobs', 'confirmModal']);
    const [initialFormValues, setInitialFormValues] = useState({});

    // #region Handlers
    const handleSubmit = values => {
        const isValuesChanged = checkValuesChanged(values, initialFormValues);

        if (!isValuesChanged || !hasUnsavedChanges) {
            return;
        }

        const newValues = {
            ...values,
            statusId: 1,
            consumerId: 1,
            language: values.language || 'en',
            brand: job?.brand, // delete this line if brand field will be added to the form
        };

        delete newValues.status; // we send only statusId to the backend

        if (mode === 'edit') {
            handleEdit(job.id, newValues).then(updatedJob => {
                if (updatedJob) {
                    setTimeout(() => {
                        form.resetFields();
                    }, 1000);
                }

                if (isClickFromView && updatedJob) {
                    setTimeout(() => {
                        // we need setTimeout here for smooth animation, drawer closing takes around 500ms, after that we will open preview drawer
                        openPreviewDrawer(updatedJob);
                    }, 500);
                }
            });
        } else {
            handleAdd(newValues, () => form.resetFields());
        }

        if (hasUnsavedChanges) {
            onHasUnsavedChanges(false);
        }
    };
    // #endregion

    // #region useEffects
    useEffect(() => {
        form.resetFields();

        if (mode === 'edit' && job) {
            const values = {
                ...job,
                status: job.status?.id,
                state: leadState[0].value,
            };

            form.setFieldsValue(values);
            setInitialFormValues(values);
        } else if (mode === 'add') {
            const values = {
                status: statuses.find(s => (s.name = 'New'))?.id,
                language: 'en',
            };

            form.setFieldsValue(values);
            setInitialFormValues(values);
        }
    }, [job, form, mode]);
    // #endregion

    return (
        <Form
            name="basic"
            layout="vertical"
            autoComplete="off"
            form={form}
            onFinish={values => {
                handleSubmit(values);
            }}
            onValuesChange={allValues => {
                handleFormValuesChange(allValues, initialFormValues, onHasUnsavedChanges);
            }}
        >
            <Flex vertical>
                <Form.Item
                    name="vin"
                    label={t('vin')}
                    rules={getInputRules(t, inputType.VIN)}
                    style={{ width: '100%' }}
                >
                    <Input
                        placeholder={t('placeholder.vin')}
                        allowClear
                        style={{ ...inputStyle, width: '100%' }}
                        maxLength={17}
                        onInput={e => {
                            e.target.value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
                        }}
                    />
                </Form.Item>
                {/* <Form.Item
                    name="brand"
                    label={t('brand')}
                    rules={getInputRules(t, inputType.BRAND, t('errors.required'), t('errors.string'))}
                    style={{ width: '100%' }}
                >
                    <Input placeholder={t('placeholder.brand')} allowClear style={{ ...inputStyle, width: '100%' }} />
                </Form.Item> */}
                <Form.Item name="model" label={t('model')} rules={getInputRules(t, inputType.MODEL)}>
                    <Input placeholder={t('placeholder.model')} allowClear style={{ ...inputStyle, width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="language"
                    label={t('language')}
                    rules={getInputRules(t, inputType.LANGUAGE, t('errors.required'), t('errors.string'))}
                >
                    <Select
                        placeholder={t('placeholder.language')}
                        style={{ width: '100%', height: '48px' }}
                        defaultValue="en"
                        options={languageOptions.map(option => ({
                            value: option.value,
                            label: <LanguageOption flag={option.flag} label={option.label} />,
                        }))}
                    />
                </Form.Item>
                <Form.Item>
                    <Button className={classes['submit-button']} type="primary" htmlType="submit" loading={loading}>
                        {mode === 'add' ? t('jobs:actions.add') : t('jobs:actions.save')}
                    </Button>
                </Form.Item>
            </Flex>
        </Form>
    );
};
